import Markdown from "react-markdown";

import { ContentCopyRounded, ThumbDownOffAltRounded, ThumbUpOutlined } from "@mui/icons-material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Box, Button, CircularProgress, IconButton, Skeleton, Typography } from "@mui/material";

import { IPSearchAnalyticsService } from "@analytics/index";

import AIChip from "@components/AIChip/AIChip";
import {
  TableToolbar,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";

import { CompanyType } from "@interfaces/company.type";

import { PopupActions } from "@store/popupStore";

import theme from "@theme/index";

import { handleCopyToClipboard } from "@utilities/copyToClipboard";

interface IAiInsightsProperties {
  company: {
    id: string;
    insight?: string | string[];
  };
  getInsight?: (companyId: CompanyType["id"]) => void;
  isInsightLoading?: boolean;
}

export default function AiInsights({ company, getInsight, isInsightLoading }: IAiInsightsProperties) {
  const handleNegativeFeedbackClick = () => {
    IPSearchAnalyticsService.trackSummaryNegativelyRated(
      Array.isArray(company.insight) ? company.insight.join("\n") : company.insight || "",
    );
    PopupActions.openAlert({
      duration: 4000,
      message: "Thank you for your feedback!",
      severity: "success",
    });
  };

  const handlePositiveFeedbackClick = () => {
    IPSearchAnalyticsService.trackSummaryPositivelyRated();
    PopupActions.openAlert({
      duration: 4000,
      message: "Thank you for your feedback!",
      severity: "success",
    });
  };
  return (
    <Box
      sx={{
        overflowY: "hidden",
      }}
    >
      <TableToolbar sx={{ border: "none" }}>
        <TableToolbarLeft>
          <TableToolbarTitle tableTitle="AI Insights" workflow="" />
        </TableToolbarLeft>
        <TableToolbarRight>
          <AIChip />
          {company.insight && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                color="success"
                disabled={isInsightLoading}
                onClick={() => getInsight?.(company.id)}
                size="medium"
                startIcon={isInsightLoading ? <CircularProgress size={20} /> : <RefreshRoundedIcon />}
                variant="contained"
              >
                Regenerate
              </Button>
              {!isInsightLoading && (
                <Button
                  color="secondary"
                  onClick={() => {
                    handleCopyToClipboard(
                      Array.isArray(company.insight) ? company.insight.join("\n") : company.insight || "",
                    );
                    IPSearchAnalyticsService.trackAIInsightCopied();
                  }}
                  size="medium"
                  startIcon={<ContentCopyRounded />}
                  variant="contained"
                >
                  Copy
                </Button>
              )}
              <>
                <IconButton onClick={handlePositiveFeedbackClick} sx={{ height: "35px", width: "35px" }}>
                  <ThumbUpOutlined
                    sx={{
                      "&:hover": { color: theme.palette.primary.main },
                      cursor: "pointer",
                      transition: "color 0.1s ease-in-out",
                    }}
                  />
                </IconButton>
                <IconButton onClick={handleNegativeFeedbackClick} sx={{ height: "35px", width: "35px" }}>
                  <ThumbDownOffAltRounded
                    sx={{
                      "&:hover": { color: theme.palette.error.main },
                      cursor: "pointer",
                      transition: "color 0.1s ease-in-out",
                    }}
                  />
                </IconButton>
              </>
            </Box>
          )}
        </TableToolbarRight>
      </TableToolbar>
      {(!company.insight && !!company.id) || isInsightLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            height: "100%",
            p: 3,
          }}
        >
          <Typography>
            <strong>Generating AI Insights...</strong>
          </Typography>
          <Skeleton animation="wave" height={24} variant="rounded" width="100%" />
          <Skeleton animation="wave" height={16} variant="rounded" width="100%" />
          <Skeleton animation="wave" height={16} variant="rounded" width="100%" />
          <Skeleton animation="wave" height={16} variant="rounded" width="100%" />
        </Box>
      ) : null}

      {company.insight && !isInsightLoading && (
        <Box sx={{ height: "calc(100vh - 350px)", overflowY: "auto", p: 3 }}>
          <Markdown>{Array.isArray(company.insight) ? company.insight.join("\n") : company.insight}</Markdown>
        </Box>
      )}
    </Box>
  );
}
